import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import { apiGet, apiPost } from '../../brainApi';
import EmailNotificationRender from '../scheduled_work/EmailNotificationRender';
import SmsNotificationRender from '../scheduled_work/SmsNotificationRender';
import { sendAnalyticsEvent } from '../../analytics';

const CustomerNotificationEmail = ({ record }) => {
  const notify = useNotify();
  const customerId = record.customer_id;
  const hasEmail = !!record.email;
  const [postmarkTemplates, setPostmarkTemplates] = useState({});
  const [currentPostmarkTemplate, setCurrentPostmarkTemplate] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [sendTo, setSendTo] = useState({sendToUser: 'customer'});

  const fetchEmailTemplates = () => {
    const params = { list_type: 'customer' };
    apiGet('/postmark/template', params).then((templates) => {
      setPostmarkTemplates(templates);
      if (templates && Object.keys(templates).length) {
        // Set state to be the id (index 0) of the first entry in the template list
        setCurrentPostmarkTemplate(Object.keys(templates)[0]);
      }
    });
  };

  useEffect(fetchEmailTemplates, []);

  const sendToMail = (event) => {
    const name = event.target.name;
    setSendTo({
      ...sendTo,
      [name] : event.target.value,
    });
  } 

  const sendEmail = (event, isTest = false) => {
    event.preventDefault();
    if(sendTo.sendToUser === 'customer'){
      isTest = false;
    }else if(sendTo.sendToUser === 'admin'){
      isTest = true;
    }
    const url = `/customer/${customerId}/send-email/${currentPostmarkTemplate}`;
    return apiPost(url, { send_as_test: !!isTest })
      .then((json) => {
        if (json.Status === 'Failure') {
          notify(`Error: ${json.Message}`, 'warning');
        } else {
          setSnackbarOpen(true);
          sendAnalyticsEvent('emailSent', {
            template: currentPostmarkTemplate,
            send_as_test: !!isTest,
          });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <EmailNotificationRender
      snackbarOpen={snackbarOpen}
      setSnackbarOpen={setSnackbarOpen}
      currentPostmarkTemplate={currentPostmarkTemplate}
      setCurrentPostmarkTemplate={setCurrentPostmarkTemplate}
      postmarkTemplates={postmarkTemplates}
      sendEmail={sendEmail}
      hasEmail={hasEmail}
      sendToMail = {sendToMail}
      sendTo={sendTo}
    />
  );
};

const CustomerNotificationSms = ({ record, smsTemplates }) => {
  const notify = useNotify();
  const customerId = record.customer_id;
  const [currentSmsTemplate, setCurrentSmsTemplate] = useState('');
  const [smsContent, setSmsContent] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [smsIsSending, setSmsIsSending] = useState(false);

  const getSmsContent = (template) => {
    setSmsContent('');
    return apiGet(`/customer/${customerId}/sms/${template}`).then((json) => {
      if (json.Status === 'Failure') {
        notify(`Error: ${json.Message}`, 'warning');
      } else {
        setSmsContent(json.Body);
      }
    });
  };

  useEffect(() => {
    if (smsTemplates && smsTemplates.length) {
      setCurrentSmsTemplate(smsTemplates[0].filename);
      getSmsContent(smsTemplates[0].filename);
    }
  }, [smsTemplates]);

  const setSmsTemplateAndContent = (event) => {
    setCurrentSmsTemplate(event.target.value);
    getSmsContent(event.target.value);
  };

  const sendSms = (event) => {
    setSmsIsSending(true);
    event.preventDefault();
    const url = `/customer/${customerId}/sms/${currentSmsTemplate}`;
    apiPost(url)
      .then((json) => {
        setSmsIsSending(false);
        if (json.Status === 'Failure') {
          notify(`Error: ${json.Message}`, 'warning');
        } else {
          setSnackbarOpen(true);
          sendAnalyticsEvent('messageSent', {
            template: currentSmsTemplate,
          });
        }
      })
      .catch((error) => {
        console.log(error);
        setSmsIsSending(false);
        notify('Error: Message failed to send', 'warning');
      });
  };

  return (
    <SmsNotificationRender
      snackbarOpen={snackbarOpen}
      setSnackbarOpen={setSnackbarOpen}
      smsTemplates={smsTemplates}
      currentSmsTemplate={currentSmsTemplate}
      setSmsTemplateAndContent={setSmsTemplateAndContent}
      smsContent={smsContent}
      sendSms={sendSms}
      smsIsSending={smsIsSending}
    />
  );
};

const useStyles = makeStyles(
  () => ({
    title: {
      fontSize: '16px',
      marginBottom: '10px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: 'bold',
      color: '#2c3252',
    },
    tabRoot: {
      '& .MuiTab-root': {
        minWidth: 100,
      },
      '& .MuiTabs-flexContainer': {
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  }),
  { name: 'communicationDetails' },
);

const TabPanel = ({ children, value, index }) => {
  return <>{value === index && children}</>;
};

const CustomerCommunicationsView = ({ record }) => {
  const classes = useStyles();
  const [communicationTabValue, setCommunicationTabValue] = useState(0);
  const [smsTemplates, setSmsTemplates] = useState([]);

  const handleCommunicationTabChange = (event, newValue) => {
    setCommunicationTabValue(newValue);
  };

  const fetchSMSTemplates = () => {
    const params = { list_type: 'customer' };
    apiGet('/mustache/template', params).then((templates) => {
      templates.sort((a, b) => {
        if (!a) {
          return b;
        }
        if (!b) {
          return a;
        }
        return a.title.localeCompare(b.title);
      });

      setSmsTemplates(templates);
    });
  };

  useEffect(fetchSMSTemplates, []);

  return (
    <>
      {smsTemplates?.length > 0 && (
        <Tabs
          value={communicationTabValue}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleCommunicationTabChange}
          aria-label="communication"
          className={classes.tabRoot}
        >
          <Tab label="Email" />
          <Tab label="SMS" />
        </Tabs>
      )}
      <Box mt={2}>
        <TabPanel value={communicationTabValue} index={0}>
          <CustomerNotificationEmail record={record} />
        </TabPanel>
        <TabPanel value={communicationTabValue} index={1}>
          <CustomerNotificationSms
            record={record}
            smsTemplates={smsTemplates}
          />
        </TabPanel>
      </Box>
    </>
  );
};

export default CustomerCommunicationsView;
